/**
 * 화면이 깨진 걸 **운영자가 알 수 있게** — 익명 오류 신호(FR-COM-05).
 *
 * ## 왜 필요한가
 * 지금까지는 아이 화면이 하얗게 떠도 아무도 몰랐다. `ErrorBoundary` 는 친절한 폴백을
 * 보여 주지만 **조용했다** — 콘솔에만 찍혔다. 운영자는 아이 폰의 콘솔을 볼 수 없다.
 * 그래서 "가끔 안 된다"는 말을 듣고도 무엇이 몇 번 깨졌는지 알 방법이 없었다.
 *
 * ## 무엇을 보내나 — **자유 문자열은 안 보낸다**(불변식 1)
 * 오류 메시지에는 무엇이든 들어갈 수 있다(닉네임이 낀 문자열, 붙여넣은 URL…).
 * 그래서 메시지 자체는 **절대 안 보낸다.** 대신:
 *
 *  - `kind`  오류 종류(`TypeError` 등) — 우리 코드가 만든 이름표
 *  - `at`    터진 자리(`WorldScene.ts:412`) — 파일명은 우리 것이라 안전
 *  - `fp`    메시지의 짧은 지문(해시) — 읽을 수는 없지만 **같은 오류인지**는 안다
 *  - `where` 화면 이름(`/verse` 등) — 미리 정한 목록 안에서만
 *
 * 이 넷이면 "어느 화면에서 어떤 오류가 몇 번" 을 셀 수 있다. 원문은 콘솔에만 남는다.
 *
 * ## 폭주 방지
 * 렌더 루프 안에서 터지면 초당 수백 번 들어온다. 같은 지문은 3번까지, 한 세션 통틀어
 * 20번까지만 보낸다 — 개수가 아니라 **무엇이 깨졌는지**가 필요한 정보다.
 */
import { track } from "./ga4";
import { ROUTES } from "./routes";

/**
 * 화면 이름 — 미리 정한 목록 밖은 `other`. 경로에 아이가 넣은 값이 섞여 나가지 않게.
 *
 * 🔴 **주소 목록을 손으로 적지 않는다.** 예전엔 손으로 적었는데 `/verse`·`/hero` 처럼
 * **없는 주소**가 들어 있고, 정작 체험공간(`/world`)·입장(`/v`)·부모 화면(`/mybook`)이
 * 빠져 있었다. 그 화면에서 깨지면 전부 `other` 로 뭉쳐서, 운영자는 **어디가 깨졌는지
 * 알 수가 없다**(2026-09-01: 하루치 `app_error` 의 절반이 `other` 였다).
 *
 * 주소를 만드는 곳(`ROUTES`)에서 가져온다 — 새 화면을 만들면 여기도 저절로 따라온다.
 */
const KNOWN_ROUTES: readonly string[] = Object.values(ROUTES);

export function routeLabel(pathname: string): string {
  if (KNOWN_ROUTES.includes(pathname)) return pathname;
  const top = `/${pathname.split("/")[1] ?? ""}`;
  return KNOWN_ROUTES.includes(top) ? top : "other";
}

/** 메시지 → 짧은 지문. 되돌릴 수 없고(원문 복원 불가), 같은 오류는 같은 값이 된다. */
export function fingerprint(text: string): string {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

/**
 * 스택 첫 줄에서 **우리 파일**의 위치만 뽑는다(`WorldScene.ts:412`).
 * 절대 URL·쿼리·해시는 버린다 — 배포 호스트나 파라미터가 섞여 나갈 이유가 없다.
 */
export function codeSite(stack: string | undefined): string | undefined {
  if (!stack) return undefined;
  for (const line of stack.split("\n").slice(0, 6)) {
    const m = /([\w.-]+\.(?:tsx?|jsx?|mjs)):(\d+)(?::\d+)?/.exec(line);
    if (m) return `${m[1]}:${m[2]}`;
  }
  return undefined;
}

const SAME_FP_MAX = 3;
const TOTAL_MAX = 20;
const seen = new Map<string, number>();
let total = 0;

/** 테스트·재입장용. 실제 앱에서는 세션 내내 누적된다. */
export function resetErrorReportLimits(): void {
  seen.clear();
  total = 0;
}

export interface ReportInput {
  error: unknown;
  /** 어디서 잡았나 — 렌더 경계인지, 전역 핸들러인지. */
  source: "boundary" | "window" | "promise";
  pathname?: string;
}

/**
 * 익명 오류 신호 1건. **절대 throw 하지 않는다** — 오류 보고가 오류를 내면 최악이다.
 * 반환값은 실제로 보냈는지(테스트·디버그용).
 */
export function reportError({ error, source, pathname }: ReportInput): boolean {
  try {
    const err = error instanceof Error ? error : undefined;
    const message = err?.message ?? (typeof error === "string" ? error : String(error));
    const kind = err?.name ?? typeof error;
    const fp = fingerprint(`${kind}|${message}`);

    // 원문은 여기(브라우저 콘솔)에만 남긴다 — 서버로는 안 나간다
    console.error(`[앱 오류/${source}]`, error);

    const n = seen.get(fp) ?? 0;
    if (n >= SAME_FP_MAX || total >= TOTAL_MAX) return false;
    seen.set(fp, n + 1);
    total++;

    track({
      name: "app_error",
      params: {
        kind: kind.slice(0, 40),
        fp,
        source,
        where: routeLabel(pathname ?? (typeof location !== "undefined" ? location.pathname : "/")),
        ...(codeSite(err?.stack) ? { at: codeSite(err?.stack)! } : {}),
      },
    });
    return true;
  } catch {
    // 보고 실패가 화면을 더 망가뜨리면 안 된다(의도된 경계)
    return false;
  }
}

/**
 * 잡히지 않은 오류까지 본다 — 렌더 밖(이벤트 핸들러·타이머·Phaser 루프·await)에서 터진 것.
 * `ErrorBoundary` 는 **렌더 중**만 잡는다. 이번 세션의 월드 정지도 렌더 밖이었다.
 */
export function installGlobalErrorReporting(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("error", (e) => {
    reportError({ error: e.error ?? e.message, source: "window" });
  });
  window.addEventListener("unhandledrejection", (e) => {
    reportError({ error: (e as PromiseRejectionEvent).reason, source: "promise" });
  });
}
